/** Zentrale i18n-Infrastruktur: eine deterministische Fallback-Kette, eine Locale-Registry,
 *  locale-abhängige Formatierung. Formatierung ist reine Präsentation und verändert nie
 *  gespeicherte Werte. */
import { createContext, useContext, useMemo, useState, useEffect, useCallback } from "react";
import { LOCALES, TRANSLATIONS, PLATFORM_FALLBACK } from "./translations";
import { normalizeLang, browserLanguage, resolveUserLocale } from "./resolve";
import { api, setLocaleContext } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export { LOCALES, PLATFORM_FALLBACK };

const SUPPORTED = LOCALES.map((l) => l.locale);
// Im localStorage liegt AUSSCHLIESSLICH die gewählte Anzeigesprache (z. B. "de") — keine Tokens,
// keine Zugangsdaten, keine personenbezogenen Daten. Das JWT liegt in einem HttpOnly-Cookie und
// ist für JavaScript nicht lesbar. Externe Code-Quality-Tools melden das hier wiederholt
// fälschlich als "Insecure Data Storage" — siehe /app/CODE_REVIEW_NOTES.md, Punkt 4.
const STORAGE_KEY = "clubops.locale";

export const intlTag = (locale) =>
  (LOCALES.find((l) => l.locale === locale) || LOCALES[0]).intl;

/** requested -> tenant.default_language -> Plattform-Fallback (eine Kette, app-weit). */
export function resolveLocale(requested, tenantDefault) {
  for (const c of [requested, tenantDefault, PLATFORM_FALLBACK]) {
    if (c && SUPPORTED.includes(c)) return c;
  }
  return PLATFORM_FALLBACK;
}

export { normalizeLang, browserLanguage, resolveUserLocale };

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  // Bewusste Auswahl (Gerät). Die Browser-Sprache landet hier NIE.
  const [requested, setRequested] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  // Am Benutzer gespeicherte Präferenz (höchste Priorität, geräteübergreifend).
  const [userPref, setUserPref] = useState(null);
  const browser = useMemo(() => browserLanguage(), []);

  useEffect(() => {
    setUserPref(user?.preferred_language || null);
  }, [user?.id, user?.preferred_language]);

  // Nach Login/Logout neu laden: die Konfiguration ist mandantenspezifisch und auf der
  // Login-Seite noch nicht abrufbar (401).
  useEffect(() => {
    if (!user) { setConfig(null); return; }
    api.get("/tenant-config")
      .then((r) => setConfig(r.data))
      .catch(() => setConfig(null));
  }, [user]);

  const enabled = useMemo(() => {
    const list = (config?.enabled_languages || []).filter((l) => SUPPORTED.includes(l));
    // Ohne Session (Login-Seite) gibt es keine Mandantenkonfiguration – dort ist jede
    // Plattformsprache wählbar, damit die gespeicherte Wunschsprache greift.
    if (list.length) return list;
    return config ? [PLATFORM_FALLBACK] : SUPPORTED;
  }, [config]);

  // Eine vom Mandanten deaktivierte Sprache ist für dessen Nutzer nicht wählbar.
  const locale = useMemo(() => resolveUserLocale({
    userPreferred: userPref,
    browser: requested || browser,
    tenantDefault: config?.default_language,
    allowed: enabled,
  }), [userPref, requested, browser, enabled, config]);

  const currency = config?.base_currency || "EUR";

  // Synchron im Render: die Formatierungshelfer in lib/api.js lesen Modul-Globals, die sonst
  // erst nach dem Render gesetzt würden und beim Sprachwechsel eine Runde zurückhinken.
  setLocaleContext(intlTag(locale), currency);

  // Papierformat des Mandanten für ALLE Druckansichten. `@page` lässt sich nicht per Klasse
  // scopen, daher wird bei US Letter eine zusätzliche Regel injiziert, die die A4-Vorgabe aus
  // index.css in der Kaskade überschreibt.
  const paperFormat = config?.paper_format || "A4";
  useEffect(() => {
    const ID = "clubops-paper-format";
    document.getElementById(ID)?.remove();
    if (paperFormat !== "LETTER") return;
    const el = document.createElement("style");
    el.id = ID;
    el.textContent = "@media print { @page { size: Letter; margin: 0; } }";
    document.head.appendChild(el);
    return () => el.remove();
  }, [paperFormat]);

  // Sprachattribut des Dokuments an die UI-Sprache binden (Screenreader + Silbentrennung).
  useEffect(() => {
    document.documentElement.setAttribute("lang", intlTag(locale));
  }, [locale]);

  // Bewusster Sprachwechsel: UI sofort umstellen und die Präferenz am Benutzer persistieren.
  const setLocale = useCallback((l) => {
    localStorage.setItem(STORAGE_KEY, l);
    setRequested(l);
    if (!user) return;
    setUserPref(l);
    api.patch("/auth/me/language", { language: l }).catch(() => {});
  }, [user]);

  /** t("key") · t("key", "Fallback") · t("key", { name: "…" }) für Platzhalter {name}. */
  const t = useCallback((key, arg) => {
    const dict = TRANSLATIONS[locale] || {};
    const fallback = typeof arg === "string" ? arg : undefined;
    let s = dict[key] ?? TRANSLATIONS[PLATFORM_FALLBACK][key] ?? fallback ?? key;
    if (arg && typeof arg === "object") {
      for (const [k, v] of Object.entries(arg)) s = s.split(`{${k}}`).join(String(v));
    }
    return s;
  }, [locale]);

  /** Zentrale Pluralisierung über CLDR-Kategorien (Intl.PluralRules).
   *  tp("plural.lines", n) wählt "<key>.<one|two|few|many|other>" und fällt auf ".other" zurück.
   *  Damit gibt es keine screenspezifischen Plural-Hacks. {count} wird immer locale-formatiert. */
  const tp = useCallback((key, count, vars) => {
    const n = Number(count) || 0;
    const cat = new Intl.PluralRules(intlTag(locale)).select(n);
    const dict = TRANSLATIONS[locale] || {};
    const base = TRANSLATIONS[PLATFORM_FALLBACK];
    const pick = (c) => dict[`${key}.${c}`] ?? base[`${key}.${c}`];
    let s = pick(cat) ?? pick("other") ?? key;
    s = s.split("{count}").join(new Intl.NumberFormat(intlTag(locale)).format(n));
    if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
    return s;
  }, [locale]);

  /** Lokalisierter Ländername über Intl.DisplayNames; ISO-Code bleibt die fachliche Wahrheit
   *  und dient als deterministischer Fallback (niemals leer). */
  const fmtCountry = useCallback((code) => {
    if (!code) return "";
    try { return new Intl.DisplayNames([intlTag(locale)], { type: "region" }).of(String(code).toUpperCase()) || String(code); }
    catch { return String(code); }
  }, [locale]);

  const value = useMemo(() => ({
    locale, setLocale, t, tp, fmtCountry, currency, enabled, config, paperFormat,
    reloadConfig: () => api.get("/tenant-config").then((r) => setConfig(r.data)).catch(() => {}),
    fmtNumber: (n, opts) => new Intl.NumberFormat(intlTag(locale), opts).format(Number(n || 0)),
    fmtMoney: (n, cur) => new Intl.NumberFormat(intlTag(locale),
      { style: "currency", currency: cur || currency }).format(Number(n || 0)),
    fmtPercent: (n) => new Intl.NumberFormat(intlTag(locale),
      { style: "percent", minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(n || 0) / 100),
    fmtDate: (s) => {
      if (!s) return "—";
      try {
        return new Date(s).toLocaleDateString(intlTag(locale),
          { day: "2-digit", month: "2-digit", year: "numeric" });
      } catch { return s; }
    },
  }), [locale, setLocale, t, tp, fmtCountry, currency, enabled, config, paperFormat]);

  // `key` erzwingt beim Sprach-/Währungswechsel ein Remount des Baums. Nur so übernehmen auch
  // Komponenten die neue Formatierung, die t() nicht selbst konsumieren (React überspringt
  // sonst das Neurendern der unveränderten children-Elemente).
  return (
    <I18nContext.Provider value={value}>
      <div className="contents">{children}</div>
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n muss innerhalb von I18nProvider verwendet werden");
  return ctx;
}
